import "./price-range-filter";

// Filter types whose param can hold more than one value at a time. Boolean and
// price_range filters hold one, so a new value replaces the current one.
const MULTI_VALUE_FILTER_TYPES = ["list"];

const SIZE_FILTER_PARAM_PREFIX = "filter.v.option.size";
const AVAILABILITY_FILTER_PARAM = "filter.v.availability";

const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", (e) => {
      this.debouncedOnSubmit(e);
    });
    facetForm.addEventListener("aria-listbox:change", (e) => {
      const productGridSection = document.getElementById(
        "product-grid-section",
      );
      this.debouncedOnSubmit(e);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
    document.addEventListener(
      "relative-link:add-filters",
      FacetFiltersForm.onAddFilters,
    );
    document.addEventListener(
      "relative-link:remove-filters",
      FacetFiltersForm.onRemoveFilters,
    );
  }

  /**
   * Whether this page can apply filters without a page load. Links only hand
   * their click over when it can.
   */
  static canFilterInPlace() {
    return Boolean(
      document.getElementById("FacetFiltersForm") &&
        document.getElementById("product-grid-section"),
    );
  }

  /**
   * Applies filter params carried by a link on the current page (see
   * relative-link) without a page load. Cancelling the event tells the link
   * this page took ownership of the click.
   *
   * Links are additive — they add to the filters already applied and never
   * remove or toggle one off. Filters that can't hold more than one value at a
   * time have nothing to add to, so there the link's value wins.
   */
  static onAddFilters(event) {
    if (!FacetFiltersForm.canFilterInPlace()) return;

    event.preventDefault();

    const params = new URLSearchParams(window.location.search);
    let changed = false;

    event.detail.filterParams.forEach(([key, value]) => {
      if (FacetFiltersForm.filterAcceptsMultipleValues(key)) {
        if (params.getAll(key).includes(value)) return;
        params.append(key, value);
      } else {
        if (params.get(key) === value) return;
        params.set(key, value);
      }

      changed = true;
    });

    if (!changed) return;

    FacetFiltersForm.renderPage(
      FacetFiltersForm.normalizeSearchParams(params.toString()),
    );
  }

  /**
   * Takes the filter params carried by a link back off (see relative-link),
   * so a link showing as active can toggle its own filter off again.
   *
   * Only the exact values the link carries are removed — values another link or
   * a facet contributed to the same param stay applied.
   */
  static onRemoveFilters(event) {
    if (!FacetFiltersForm.canFilterInPlace()) return;

    event.preventDefault();

    const removals = event.detail.filterParams;
    const params = new URLSearchParams();
    let changed = false;

    // Rebuilt rather than deleted from, to keep the params in their current
    // order — URLSearchParams.delete drops every value a key holds.
    new URLSearchParams(window.location.search).forEach((value, key) => {
      const isRemoved = removals.some(
        ([removedKey, removedValue]) =>
          removedKey === key && removedValue === value,
      );

      if (isRemoved) {
        changed = true;
        return;
      }

      params.append(key, value);
    });

    if (!changed) return;

    FacetFiltersForm.renderPage(
      FacetFiltersForm.normalizeSearchParams(params.toString()),
    );
  }

  /**
   * Whether a filter param can hold more than one value at a time, based on the
   * type the facets form declares for it. Params with no rendered facet are
   * assumed to stack, so an applied value is never replaced blindly.
   */
  static filterAcceptsMultipleValues(paramName) {
    const filter = document.querySelector(
      `#FacetFiltersForm [data-filter-params~="${paramName}"]`,
    );

    if (!filter) return true;

    return MULTI_VALUE_FILTER_TYPES.includes(filter.dataset.filterType);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    FacetFiltersForm.setLoading();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    // The URL is now the applied filters, so anything reflecting them can
    // re-read it — active filter links, for one (see relative-link).
    document.dispatchEvent(
      new CustomEvent("filters:changed", { detail: { searchParams } }),
    );
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.setDoneLoading();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.setDoneLoading();
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter",
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const details = element.querySelector(".facets__item-details");
      const target = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`,
      );
      if (!target) return;
      const targetDetails = target.querySelector(".facets__item-details");

      if (details && targetDetails) {
        if (targetDetails.hasAttribute("open")) {
          details.setAttribute("open", "");
          details.classList.add("open");
        } else {
          details.removeAttribute("open");
          details.classList.remove("open");
        }
      }

      target.innerHTML = element.innerHTML;
    });

    // FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter"),
      );
  }

  // static renderActiveFacets(html) {
  //   const activeFacetElementSelectors = [
  //     "[data-active-filters-mobile]",
  //     "[data-active-filters-desktop]",
  //   ];

  //   activeFacetElementSelectors.forEach((selector) => {
  //     const activeFacetsElement = html.querySelector(selector);
  //     if (!activeFacetsElement) return;

  //     document.querySelectorAll(selector).forEach((el) => {
  //       el.innerHTML = activeFacetsElement.innerHTML;
  //     });
  //   });
  // }

  static renderAdditionalElements(html) {
    const additionalElements = html.querySelectorAll("[data-render]");

    additionalElements.forEach((el) => {
      const selector = `[data-render="${el.dataset.render}"]`;
      const target = document.querySelector(selector);

      if (!target) return;
      target.innerHTML = html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid-section").dataset.id,
      },
    ];
  }

  static setLoading() {
    document.querySelector("[data-product-grid]").classList.add("loading");
  }

  static setDoneLoading() {
    document.querySelector("[data-product-grid]").classList.remove("loading");
  }

  /**
   * A size filter is only meaningful alongside the in-stock filter, since a
   * size that sold out isn't a size the customer can buy — so availability
   * rides along with it and comes off with it.
   */
  static normalizeSearchParams(paramStr) {
    const params = new URLSearchParams(paramStr);
    const hasSizeFilter = Array.from(params.keys()).some((key) =>
      key.startsWith(SIZE_FILTER_PARAM_PREFIX),
    );

    if (hasSizeFilter) {
      if (!params.has(AVAILABILITY_FILTER_PARAM)) {
        params.append(AVAILABILITY_FILTER_PARAM, "1");
      }
    } else {
      params.delete(AVAILABILITY_FILTER_PARAM);
    }

    return params.toString();
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return FacetFiltersForm.normalizeSearchParams(
      new URLSearchParams(formData).toString(),
    );
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form"),
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];

      sortFilterForms.forEach((form) => {
        if (
          form.id === "FacetSortForm" ||
          form.id === "FacetFiltersForm" ||
          form.id === "FacetSortDrawerForm"
        ) {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this)),
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
